import React, {
  MouseEvent,
  ReactChild,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import service from './service'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { AppContext } from '../../../contexts/AppContext'
import { Campaign, CurrentCampaign } from '../../Campaign/CampaignTable/types'
import { Filter } from '../../../hooks/useTableFilter'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store'
import voohCampaignTableFiltersService from '../VoohCampaignTableFilters/service'
import { CampaignPermissionType, FeatureName } from '../../../types/features'
import { CampaignSortColumn, CampaignStatus, VoohCampaign } from '../../../types/campaign'
import CampaignUtils from '../../../utils/campaign'
import IconBox from '../../../components/Icon/IconBox'
import Tooltip from '../../../components/Tooltip'
import Button from '../../../components/Form/Button'
import { faEdit, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import { Routes } from '../../../routes'
import Table, {
  TableBody,
  TableData,
  TableHeader,
  TableHeaderItem,
  TableRow,
} from '../../../components/Table'
import DateUtils from '../../../utils/date'
import { DATE_FORMAT } from '../../../constant'
import FillingSpinner, { FillingSpinnerType } from '../../../components/FillingSpinner'
import Pagination from '../../../components/Pagination'
import PageHeader from '../../../components/Layout/PageHeader'
import ModalAction from '../../../components/Modal/ModalAction'
import BroadSignAddModal from '../../Campaign/CampaignTable/BroadSignAddModal'
import VoohCampaignTableFilters from '../VoohCampaignTableFilters'
import { VoohCampaignTableItem } from './types'
import { CAMPAIGN_ACTION } from '../../../constant/authorization'

const ITEMS_PER_PAGE = 100

const VoohCampaignTable: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { getTableFilter, setTableFilter, allowedFor } = useContext(AppContext)
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [totalCount, setTotalCount] = useState<number>(0)
  const [campaigns, setCampaigns] = useState<VoohCampaignTableItem[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [trigger, setTrigger] = useState<boolean>(false)
  const [currentCampaign, setCurrentCampaign] = useState<CurrentCampaign>()
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState<boolean>(false)
  const [editModalIsOpenForCampaign, setEditModalIsOpenForCampaign] = useState<Campaign['id']>()
  const [removeCampaignLoading, setRemoveCampaignLoading] = useState<boolean>(false)
  const tableFilter = getTableFilter(Filter.VOOH_CAMPAIGN)
  const [hoverCampaignName, setHoverCampaignName] = useState<Campaign['id']>()
  const [hoverAgencyName, setHoverAgencyName] = useState<Campaign['id']>()
  const [hoverSupervisors, setHoverSupervisors] = useState<Campaign['id']>()
  const [isBroadSignModalOpen, setIsBroadSignOpen] = useState<boolean>(false)

  const tableScrollRef = useRef<HTMLDivElement>(null)
  const floatingScrollbarRef = useRef<HTMLDivElement>(null)
  const isSyncingScrollRef = useRef(false)
  const [tableScrollWidth, setTableScrollWidth] = useState(0)
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false)

  const { voohCampaignfilters } = useSelector((rs: RootState) => rs.voohCampaignTableFilters)
  const { addNotification } = useContext(AppContext)

  const getCampaigns = (): void =>
    service.getVoohCampaigns({
      pageNumber,
      itemsPerPage: ITEMS_PER_PAGE,
      setCampaigns,
      setTotalCount,
      setLoading,
      setTrigger,
      tableFilter,
      filters: voohCampaignTableFiltersService.pickOnlyApiFilters(voohCampaignfilters),
    })

  useEffect(() => {
    if (trigger) {
      getCampaigns()
    }
  }, [trigger])

  useEffect(() => {
    getCampaigns()
  }, [pageNumber, tableFilter])

  const Status = (campaignStatus: Campaign['status']): ReactChild => {
    const parsedStatus = CampaignUtils.parseStatus(campaignStatus)

    return (
      <div className='CampaignTable__status'>
        <IconBox
          className='CampaignTable__status--icon'
          icon={parsedStatus.icon}
          theme={parsedStatus.iconTheme}
        />
        {parsedStatus.text}
      </div>
    )
  }

  const Buttons = (
    campaignId: VoohCampaign['id'],
    campaignName: VoohCampaign['name'],
    campaignStatus: VoohCampaign['status']
  ): ReactChild => (
    <>
      <>
        <div>
          <Button
            icon={faEdit}
            tooltip={t('common.edit')}
            dataCy={'edit-campaign-btn'}
            onClick={() => {
              if (campaignStatus === CampaignStatus.ACTIVE) {
                setEditModalIsOpenForCampaign(campaignId)
              } else {
                navigate(Routes.VOOH_CAMPAIGNS.EDIT(campaignId))
              }
            }}
            disabled={
              !allowedFor({
                template: CAMPAIGN_ACTION.EDIT,
                feature: { name: FeatureName.Campaigns, action: CampaignPermissionType.Write },
                status: campaignStatus,
              })
            }
          />
        </div>
      </>
      <Button
        icon={faTrashAlt}
        tooltip={t('common.remove')}
        dataCy={'show-remove-campaign-modal-button'}
        onClick={() => {
          setCurrentCampaign({ id: campaignId, name: campaignName })
          setIsRemoveModalOpen(true)
        }}
        disabled={
          !allowedFor({
            feature: { name: FeatureName.Campaigns, action: CampaignPermissionType.Delete },
            status: campaignStatus,
          })
        }
      />
    </>
  )

  const nameTooltip = (campaignId: Campaign['id'], name: string) => {
    return (
      <>
        {hoverCampaignName === campaignId && (
          <div className={'CampaignTable__name--tooltip'}>
            <Tooltip>{name}</Tooltip>
          </div>
        )}
      </>
    )
  }

  const agencyTooltip = (campaignId: Campaign['id'], agencyName: string) => {
    return (
      <>
        {hoverAgencyName === campaignId && (
          <div className={'CampaignTable__agency-name--tooltip'}>
            <Tooltip>{agencyName}</Tooltip>
          </div>
        )}
      </>
    )
  }

  const supervisorsTooltip = (campaignId: Campaign['id'], supervisors: string[]) => {
    return (
      <>
        {hoverSupervisors === campaignId && (
          <div className={'CampaignTable__supervisors-cell--tooltip'}>
            <Tooltip>
              {supervisors.map((s, i) => (
                <div key={i}>{s}</div>
              ))}
            </Tooltip>
          </div>
        )}
      </>
    )
  }

  useEffect(() => {
    const scrollContainer = tableScrollRef.current
    if (!scrollContainer) return

    const updateScrollMetrics = (): void => {
      setTableScrollWidth(scrollContainer.scrollWidth)
      setHasHorizontalOverflow(scrollContainer.scrollWidth > scrollContainer.clientWidth)
    }

    updateScrollMetrics()

    const resizeObserver = new ResizeObserver(updateScrollMetrics)
    resizeObserver.observe(scrollContainer)
    window.addEventListener('resize', updateScrollMetrics)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', updateScrollMetrics)
    }
  }, [campaigns])

  // The table can grow taller than the viewport, which pushes its native
  // horizontal scrollbar (at the bottom of `.CampaignTable__table-scroll`)
  // off-screen until the page is scrolled all the way down. This mirrors the
  // scroll position into a second, sticky scrollbar that stays reachable at
  // the bottom of the viewport instead.
  const handleTableScroll = (): void => {
    if (isSyncingScrollRef.current) {
      isSyncingScrollRef.current = false
      return
    }
    if (!tableScrollRef.current || !floatingScrollbarRef.current) return
    isSyncingScrollRef.current = true
    floatingScrollbarRef.current.scrollLeft = tableScrollRef.current.scrollLeft
  }

  const handleFloatingScrollbarScroll = (): void => {
    if (isSyncingScrollRef.current) {
      isSyncingScrollRef.current = false
      return
    }
    if (!tableScrollRef.current || !floatingScrollbarRef.current) return
    isSyncingScrollRef.current = true
    tableScrollRef.current.scrollLeft = floatingScrollbarRef.current.scrollLeft
  }

  const CampaignTable: ReactChild = (
    <div className='CampaignTable__table-wrapper'>
      <div
        className='CampaignTable__table-scroll CampaignTable__table-scroll--vooh'
        ref={tableScrollRef}
        onScroll={handleTableScroll}
      >
        <Table
          className='CommonTable'
          sort={tableFilter}
          onSort={({ column, direction }) =>
            void setTableFilter(Filter.VOOH_CAMPAIGN, { column, direction })
          }
          colgroup={
            <colgroup>
              <col style={{ width: '23.3%' }} />
              <col style={{ width: '18.7%' }} />
              <col style={{ width: '16.4%' }} />
              <col style={{ width: '12.9%' }} />
              <col style={{ width: '15.6%' }} />
              <col style={{ width: '13.1%' }} />
            </colgroup>
          }
        >
          <TableHeader>
            <TableHeaderItem
              className='CommonTable__header-item'
              sortBy={CampaignSortColumn.NAME}
            >
              {t('common.name')}
            </TableHeaderItem>
            <TableHeaderItem
              className='CommonTable__header-item'
              sortBy={CampaignSortColumn.AGENCY_NAME}
            >
              {t('common.agency')}
            </TableHeaderItem>
            <TableHeaderItem
              className='CommonTable__header-item CampaignTable__date-cell'
              sortBy={CampaignSortColumn.START_DATE}
            >
              {t('campaigns.time')}
            </TableHeaderItem>
            <TableHeaderItem
              className='CommonTable__header-item CampaignTable__status-cell'
              sortBy={CampaignSortColumn.STATUS}
            >
              {t('common.status')}
            </TableHeaderItem>
            <TableHeaderItem className='CommonTable__header-item'>
              {t('common.supervisors')}
            </TableHeaderItem>
            <TableHeaderItem className='CommonTable__header-item'>
              {t('common.actions')}
            </TableHeaderItem>
          </TableHeader>

          <TableBody>
            {campaigns.map((campaign: VoohCampaignTableItem) => {
              const date: string =
                DateUtils.parseAndFormat(campaign.startDate, DATE_FORMAT) +
                ' - ' +
                DateUtils.parseAndFormat(campaign.endDate, DATE_FORMAT)

              return (
                <TableRow
                  key={campaign.id}
                  className='CommonTable__data-row'
                  onClick={(): void => void navigate(Routes.VOOH_CAMPAIGNS.ONE(campaign.id))}
                >
                  <TableData
                    className='CommonTable__data-item CommonTable__data-item--name CampaignTable__name--td'
                    onMouseEnter={(element: MouseEvent<HTMLElement>) => {
                      const currentTarget = element.currentTarget
                      const isOverflowing = currentTarget.clientWidth < currentTarget.scrollWidth
                      if (isOverflowing) {
                        setHoverCampaignName(campaign.id)
                      }
                    }}
                    onMouseLeave={() => {
                      setHoverCampaignName(undefined)
                    }}
                  >
                    <span className='CampaignTable__name--text'>{campaign.name}</span>
                    {hoverCampaignName && nameTooltip(campaign.id, campaign.name)}
                  </TableData>
                  <TableData
                    className='CommonTable__data-item CampaignTable__agency-name--td'
                    onMouseEnter={(element: MouseEvent<HTMLElement>) => {
                      const currentTarget = element.currentTarget
                      const isOverflowing = currentTarget.clientWidth < currentTarget.scrollWidth
                      if (isOverflowing) {
                        setHoverAgencyName(campaign.id)
                      }
                    }}
                    onMouseLeave={() => {
                      setHoverAgencyName(undefined)
                    }}
                  >
                    {campaign.agencyName}
                    {hoverAgencyName && agencyTooltip(campaign.id, campaign.agencyName)}
                  </TableData>
                  <TableData className='CommonTable__data-item CampaignTable__date-cell'>
                    {date}
                  </TableData>
                  <TableData className='CommonTable__data-item CampaignTable__status-cell'>
                    {Status(campaign.status)}
                  </TableData>
                  <TableData
                    className='CommonTable__data-item CampaignTable__supervisors-cell'
                    onMouseEnter={() => {
                      if (campaign.supervisors.length > 1) {
                        setHoverSupervisors(campaign.id)
                      }
                    }}
                    onMouseLeave={() => {
                      setHoverSupervisors(undefined)
                    }}
                  >
                    {campaign.supervisors.length > 0 && (
                      <>
                        {campaign.supervisors[0]}
                        {campaign.supervisors.length > 1 && ' ...'}
                      </>
                    )}
                    {hoverSupervisors && supervisorsTooltip(campaign.id, campaign.supervisors)}
                  </TableData>
                  <TableData className='CommonTable__data-item CommonTable__data-item--buttons'>
                    {Buttons(campaign.id, campaign.name, campaign.status)}
                  </TableData>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
      {hasHorizontalOverflow && (
        <div className='CampaignTable__floating-scrollbar'>
          <div
            className='CampaignTable__floating-scrollbar-inner'
            ref={floatingScrollbarRef}
            onScroll={handleFloatingScrollbarScroll}
          >
            <div style={{ width: tableScrollWidth, height: 1 }} />
          </div>
        </div>
      )}
    </div>
  )

  const CampaignTableContainer = (): ReactNode =>
    loading ? (
      <FillingSpinner type={FillingSpinnerType.STANDARD} />
    ) : campaigns.length > 0 ? (
      <>
        {CampaignTable}

        <Pagination
          activePage={pageNumber}
          itemsCountPerPage={ITEMS_PER_PAGE}
          totalItemsCount={totalCount}
          onChange={setPageNumber}
        />
      </>
    ) : (
      t('common.noDataFound')
    )

  return (
    <div className='CommonTable__container'>
      <PageHeader
        allowedFor={allowedFor({
          feature: { name: FeatureName.Campaigns, action: CampaignPermissionType.Write },
        })}
        link={Routes.VOOH_CAMPAIGNS.NEW}
        linkTooltip={t('common.createCampaign')}
      >
        <p>{t('common.voohCampaigns')}</p>
      </PageHeader>

      <VoohCampaignTableFilters
        onSubmit={() => {
          setPageNumber(1)
          setTrigger(true)
        }}
      />

      {CampaignTableContainer()}

      {currentCampaign && isRemoveModalOpen && (
        <ModalAction
          title={t('modal.campaignRemoval')}
          itemText={t('modal.doYouWantToRemove')}
          itemName={currentCampaign.name}
          actionText={t('common.remove')}
          isOpen={isRemoveModalOpen}
          setIsOpen={setIsRemoveModalOpen}
          isLoading={removeCampaignLoading}
          setIsLoading={setRemoveCampaignLoading}
          onAction={() =>
            service.deleteCampaign(currentCampaign.id, setTrigger, addNotification, t)
          }
        />
      )}

      {editModalIsOpenForCampaign && (
        <ModalAction
          title={t('modal.campaignEditing')}
          contentText={t('modal.editCampaignInfo')}
          itemText={t('modal.doYouWant')}
          itemName={t('modal.editCampaignItem')}
          actionText={t('common.edit')}
          isOpen={!!editModalIsOpenForCampaign}
          setIsOpen={() => void setEditModalIsOpenForCampaign(undefined)}
          onAction={() =>
            new Promise(() => void navigate(Routes.VOOH_CAMPAIGNS.EDIT(editModalIsOpenForCampaign)))
          }
        />
      )}

      {isBroadSignModalOpen && (
        <BroadSignAddModal
          isOpen={isBroadSignModalOpen}
          setIsOpen={setIsBroadSignOpen}
        />
      )}
    </div>
  )
}

export default VoohCampaignTable
